import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useUnlockCode } from '@/hooks/useUnlockCode'
import { supabase } from '@/integrations/supabase/client'
import { X, LogOut } from 'lucide-react'

interface UnlockCodeModalProps {
  onSuccess: () => void
  onClose?: () => void
}

const UnlockCodeModal: React.FC<UnlockCodeModalProps> = ({ onSuccess, onClose }) => {
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const { loading, validateAndRedeemCode } = useUnlockCode()
  const navigate = useNavigate()

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/auth/login')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    
    const result = await validateAndRedeemCode(code)
    
    if (result.success) {
      onSuccess()
    } else {
      // Check if the error is about pending admin approval
      if (result.error?.includes('pending') || result.error?.includes('admin has not confirmed')) {
        setError('Your payment is being verified by Dably. Please check back in a few minutes.')
      } else {
        setError(result.error || 'Invalid code')
      }
    }
  }

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase()
    // Extract only alphanumeric characters
    let chars = value.replace(/[^A-Z0-9]/g, '')
    
    // Format as DABLY-XXXX-YYYY (5 chars + 2 hyphens + 8 chars = 15 total)
    let formatted = ''
    
    if (chars.length <= 5) {
      // Just the prefix (DABLY)
      formatted = chars
    } else if (chars.length <= 9) {
      // DABLY-XXXX (5 + 1 + 4)
      formatted = chars.substring(0, 5) + '-' + chars.substring(5)
    } else {
      // DABLY-XXXX-YYYY (5 + 1 + 4 + 1 + 4 = 15)
      formatted = chars.substring(0, 5) + '-' + chars.substring(5, 9) + '-' + chars.substring(9, 13)
    }
    
    setCode(formatted)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl font-bold">Unlock Your Account</CardTitle>
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <CardDescription>
            Enter your unlock code to access the platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="unlock-code">Unlock Code</Label>
              <Input
                id="unlock-code"
                type="text"
                placeholder="DABLY-XXXX-YYYY"
                value={code}
                onChange={handleCodeChange}
                maxLength={15}
                className="text-center font-mono text-lg tracking-wider"
                disabled={loading}
                required
              />
              <p className="text-sm text-gray-500 text-center">
                Format: DABLY-XXXX-YYYY
              </p>
            </div>
            
            {error && (
              <div className="text-sm text-red-600 text-center bg-red-50 p-2 rounded">
                {error}
              </div>
            )}
            
            <Button
              type="submit"
              className="w-full"
              disabled={loading || code.length !== 15}
            >
              {loading ? 'Verifying...' : 'Unlock Account'}
            </Button>
          </form>
          
          <div className="mt-4 space-y-3 text-center">
            <p className="text-sm text-gray-600">
              Don't have a code?{' '}
              <Link 
                to="/auth/payment-info" 
                className="text-purple-600 hover:underline"
              >
                Complete your payment
              </Link>
            </p>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-gray-500 hover:text-gray-700"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default UnlockCodeModal
